/**
 * Song library — named pattern presets for the beat scheduler.
 *
 * A Song is data: drum/bass/lead/arp patterns plus pad chord progression,
 * all expressed as scale degrees in A natural minor. The scheduler reads
 * these arrays each step. Levels reference a song by id via
 * `settings.music.song`. Adding a new song means adding an entry here.
 *
 * Pattern shape:
 *   - 4 bars × 16 sixteenth-note steps
 *   - Bass / lead / arp: scale degree, -1 = rest. 0 = root (A in A minor).
 *   - Drums: 16-char string per bar. K=kick, S=snare, H=closed hat,
 *     O=open hat, .=rest.
 *   - Pad: one chord per bar, each chord an array of scale degrees
 *     played together (e.g. [0,2,4] = i, [3,5,7] = iv).
 *
 * Octave hints (consumed by the scheduler):
 *   bass octave 2, pad octave 3, lead/arp octave 4.
 */

export interface Song {
  /** Stable identifier used in level JSON. */
  id: string;
  /** Display name for menus/editor. */
  name: string;
  /** One-line vibe description for the level editor. */
  description: string;
  /** Tempo this song is composed for. Levels may override via music.bpm. */
  recommendedBpm: number;
  /** 4 bars × 16 steps. Scale degrees relative to the song's root. */
  bass: number[][];
  /** 4 bars × 16-char drum strings. */
  drums: string[];
  /** 4 bars × 16 steps. */
  lead: number[][];
  /** 4 bars × 16 steps. */
  arp: number[][];
  /** 4 bars × chord (each chord is an array of scale degrees). */
  pad: number[][];
}

// ─── Songs ─────────────────────────────────────────────────

/** Neon Drive — the original synthwave groove. Punchy, melodic, classic. */
const NEON_DRIVE: Song = {
  id: 'neon-drive',
  name: 'Neon Drive',
  description: 'Classic synthwave groove — driving bass and a singing lead.',
  recommendedBpm: 120,
  bass: [
    [0, -1, -1, -1, 0, -1, -1, -1, 0, -1, -1, -1, 0, -1, 0, -1],
    [0, -1, -1, -1, 3, -1, -1, -1, 4, -1, -1, -1, 3, -1, -1, -1],
    [0, -1, -1, -1, 0, -1, 2, -1, 4, -1, -1, -1, 2, -1, -1, -1],
    [4, -1, -1, -1, 3, -1, -1, -1, 2, -1, -1, -1, 0, -1, -1, -1],
  ],
  drums: [
    'K.H.S.H.K.H.S.HO',
    'K.H.S.H.K.HOS.H.',
    'K.H.S.HOK.H.S.H.',
    'K.HOS.H.K.H.S.HO',
  ],
  lead: [
    [0, -1, 2, -1, 4, -1, 2, -1, 0, -1, -1, -1, -1, -1, -1, -1],
    [4, -1, 5, -1, 4, -1, 2, -1, 0, -1, -1, -1, -1, -1, -1, -1],
    [0, -1, 4, -1, 7, -1, 4, -1, 2, -1, 4, -1, 0, -1, -1, -1],
    [-1, -1, -1, -1, 2, -1, 4, -1, 5, -1, 4, -1, 2, -1, 0, -1],
  ],
  arp: [
    [0, 2, 4, 7, 0, 2, 4, 7, 0, 2, 4, 7, 0, 2, 4, 7],
    [0, 3, 4, 7, 0, 3, 4, 7, 0, 3, 4, 7, 0, 3, 4, 7],
    [0, 2, 4, 7, 4, 2, 0, 2, 4, 7, 4, 2, 0, 2, 4, 7],
    [4, 2, 0, -3, 0, 2, 4, 7, 4, 2, 0, -3, 0, 2, 4, 7],
  ],
  pad: [
    [0, 2, 4],
    [3, 5, 7],
    [5, 7, 9],
    [4, 6, 8],
  ],
};

/** Crystal Cave — slow, dreamy, pad-led. For exploration moments. */
const CRYSTAL_CAVE: Song = {
  id: 'crystal-cave',
  name: 'Crystal Cave',
  description: 'Slow and dreamy — held pads with a shimmering arpeggio.',
  recommendedBpm: 96,
  bass: [
    [0, -1, -1, -1, -1, -1, -1, -1, 4, -1, -1, -1, -1, -1, -1, -1],
    [-3, -1, -1, -1, -1, -1, -1, -1, 0, -1, -1, -1, -1, -1, -1, -1],
    [2, -1, -1, -1, -1, -1, -1, -1, 4, -1, -1, -1, -1, -1, -1, -1],
    [4, -1, -1, -1, -1, -1, -1, -1, 0, -1, -1, -1, -1, -1, -1, -1],
  ],
  drums: [
    'K.......S.......',
    'K.......S.....H.',
    'K.......S.......',
    'K...H...S.....HO',
  ],
  lead: [
    [4, -1, -1, -1, -1, -1, -1, -1, 7, -1, -1, -1, -1, -1, -1, -1],
    [9, -1, -1, -1, 7, -1, -1, -1, 4, -1, -1, -1, -1, -1, -1, -1],
    [2, -1, -1, -1, -1, -1, -1, -1, 4, -1, -1, -1, 7, -1, -1, -1],
    [-1, -1, -1, -1, 4, -1, 2, -1, 0, -1, -1, -1, -1, -1, -1, -1],
  ],
  arp: [
    [0, -1, 4, -1, 7, -1, 11, -1, 7, -1, 4, -1, 0, -1, -1, -1],
    [-3, -1, 0, -1, 4, -1, 9, -1, 4, -1, 0, -1, -3, -1, -1, -1],
    [2, -1, 4, -1, 7, -1, 9, -1, 7, -1, 4, -1, 2, -1, -1, -1],
    [4, -1, 7, -1, 11, -1, 9, -1, 7, -1, 4, -1, 0, -1, -1, -1],
  ],
  pad: [
    [0, 2, 4],
    [-3, 0, 2],
    [2, 4, 7],
    [4, 7, 9],
  ],
};

/** Pulse Grid — fast, percussive, 16th-note arp. For tense, technical levels. */
const PULSE_GRID: Song = {
  id: 'pulse-grid',
  name: 'Pulse Grid',
  description: 'Fast and tense — sixteenth-note arp over four-on-the-floor.',
  recommendedBpm: 140,
  bass: [
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 4, 4, 4, 4, 0, 0, 0, 0, 4, 4, 4, 4],
    [0, 0, 0, 0, 2, 2, 2, 2, 4, 4, 4, 4, 2, 2, 2, 2],
    [3, 3, 3, 3, 0, 0, 0, 0, 4, 4, 4, 4, 3, 3, 3, 3],
  ],
  drums: [
    'KHSHKHSHKHSHKHSH',
    'KHSHKHSHKHSHKHSO',
    'KHSHKHSHKHSHKHSH',
    'KOSHKHSHKHSHKHSO',
  ],
  lead: [
    [0, 2, 4, 2, 0, 2, 4, 2, 0, 2, 4, 7, 4, 2, 0, -1],
    [4, 5, 7, 5, 4, 2, 0, 2, 4, 5, 7, 9, 7, 5, 4, -1],
    [-1, -1, 0, 2, 4, 7, 4, 2, 0, 2, 4, 7, 9, 7, 4, 2],
    [7, 4, 7, 4, 2, 0, 2, 4, 5, 4, 2, 0, -3, 0, 2, 4],
  ],
  arp: [
    [0, 2, 4, 7, 4, 2, 0, 2, 0, 2, 4, 7, 4, 2, 0, 2],
    [0, 3, 4, 7, 4, 3, 0, 3, 0, 3, 4, 7, 4, 3, 0, 3],
    [0, 4, 7, 11, 7, 4, 0, 4, 2, 4, 7, 11, 7, 4, 2, 4],
    [4, 7, 11, 9, 7, 4, 0, -3, 0, 4, 7, 11, 7, 4, 0, -3],
  ],
  pad: [
    [0, 2, 4],
    [0, 2, 4],
    [3, 5, 7],
    [4, 6, 8],
  ],
};

/** Standby — menu ambience. Pad-led, sparse arp, no drums. Held back. */
const STANDBY: Song = {
  id: 'standby',
  name: 'Standby',
  description: 'Menu ambience — held pads under a sparse arpeggio.',
  recommendedBpm: 80,
  bass: [
    [0, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [-3, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
  ],
  // Silent — included for completeness; menu plays only `pad` and `arp` layers.
  drums: [
    '................',
    '................',
    '................',
    '................',
  ],
  lead: [
    [4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [7, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [2, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
    [4, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1],
  ],
  arp: [
    [0, -1, -1, -1, 4, -1, -1, -1, 7, -1, -1, -1, 4, -1, -1, -1],
    [-3, -1, -1, -1, 0, -1, -1, -1, 4, -1, -1, -1, 0, -1, -1, -1],
    [2, -1, -1, -1, 4, -1, -1, -1, 7, -1, -1, -1, 4, -1, -1, -1],
    [4, -1, -1, -1, 7, -1, -1, -1, 9, -1, -1, -1, 7, -1, -1, -1],
  ],
  pad: [
    [0, 2, 4],
    [-3, 0, 2],
    [2, 4, 7],
    [4, 7, 9],
  ],
};

/** Deep Current — half-time, bass-heavy, sparse. For complex/imaginary moods. */
const DEEP_CURRENT: Song = {
  id: 'deep-current',
  name: 'Deep Current',
  description: 'Half-time and bass-heavy — sparse stabs over rolling drums.',
  recommendedBpm: 128,
  bass: [
    [0, -1, -1, 0, -1, -1, 0, -1, -1, -1, 0, -1, -1, 0, -1, -1],
    [-3, -1, -1, -3, -1, -1, -3, -1, -1, -1, 0, -1, -1, -3, -1, -1],
    [4, -1, -1, 4, -1, -1, 4, -1, -1, -1, 4, -1, -1, 2, -1, -1],
    [3, -1, -1, 3, -1, -1, 0, -1, -1, -1, 3, -1, -1, 0, -1, -1],
  ],
  drums: [
    'K.H.....S.H.K.H.',
    'K.H.....S.H.K.HO',
    'KOH.....S.H.K.H.',
    'K.H.K...S.H.K.HO',
  ],
  lead: [
    [-1, -1, -1, -1, -1, -1, -1, -1, 4, -1, -1, -1, -1, -1, 7, -1],
    [-1, -1, -1, -1, -1, -1, -1, -1, 4, -1, 5, -1, 7, -1, -1, -1],
    [-1, -1, -1, -1, -1, -1, -1, -1, 7, -1, 4, -1, 2, -1, -1, -1],
    [-1, -1, -1, -1, -1, -1, -1, -1, 0, -1, 2, -1, 4, -1, -1, -1],
  ],
  arp: [
    [0, -1, -1, -1, 4, -1, -1, -1, -1, -1, -1, -1, 7, -1, -1, -1],
    [-3, -1, -1, -1, 0, -1, -1, -1, -1, -1, -1, -1, 4, -1, -1, -1],
    [2, -1, -1, -1, 4, -1, -1, -1, -1, -1, -1, -1, 9, -1, -1, -1],
    [4, -1, -1, -1, 0, -1, -1, -1, -1, -1, -1, -1, 7, -1, -1, -1],
  ],
  pad: [
    [0, 2, 4],
    [-3, 0, 2],
    [4, 6, 8],
    [3, 5, 7],
  ],
};

// ─── Library ───────────────────────────────────────────────

const SONGS: Record<string, Song> = {
  [NEON_DRIVE.id]: NEON_DRIVE,
  [CRYSTAL_CAVE.id]: CRYSTAL_CAVE,
  [PULSE_GRID.id]: PULSE_GRID,
  [DEEP_CURRENT.id]: DEEP_CURRENT,
  [STANDBY.id]: STANDBY,
};

/** The fallback song used when a level omits `music.song` or names an unknown song. */
export const DEFAULT_SONG_ID = NEON_DRIVE.id;

/** Look up a song by id; falls back to the default if missing. */
export function getSong(id: string | undefined): Song {
  if (id && SONGS[id]) return SONGS[id];
  return SONGS[DEFAULT_SONG_ID];
}

/** All level-selectable songs, in display order (for editor menus). */
export function listSongs(): Song[] {
  return [NEON_DRIVE, CRYSTAL_CAVE, PULSE_GRID, DEEP_CURRENT];
}
